import { useEffect, useId, useRef, useState } from "react";
import { formatQuotedPrice } from "@shared-core/domain/formatters";
import { poFormatHint } from "@shared-core/domain/credit-control-presentation";
import { DELIVERY_NOT_YET_PRICED } from "@shared-core/domain/checkout-delivery";
import type { QuotePageView } from "@shared-core/data/customer-link-adapter";
import { Button } from "../../app/components/primitives/button";
import { Card } from "../../app/components/primitives/card";
import { cn } from "../../app/components/primitives/utils";
import { AlertTriangle, CheckCircle2, ChevronUp, XCircle } from "lucide-react";
import { BottomSheet } from "./BottomSheet";
import { SummaryTotals } from "./SummaryTotals";
import { PaymentMethodChoice, type PaymentMethod } from "./PaymentMethodChoice";
import { PoNumberField } from "./PoNumberField";

// The decision area of the customer's quote page (Brad, 8 Oct): everything
// needed to accept or decline, shown once and never with the lines again (the
// lines are the page's own, under its tabs).
//   - Wide (lg): a summary card in the page's top band (SummaryPageFrame), like the catalogue
//     item page's buy box.
//   - Narrow: a slim fixed bar (the total and Accept quote ›); tapping it opens
//     a short sheet with the totals, how to pay, the PO number, Decline… and
//     Accept. The sheet holds no line list.

/** What the decision area edits of the page's choices. */
export interface DecisionChoices {
  method: PaymentMethod | null;
  po: string;
  /** Field → message, as the page binds them. */
  errors: Record<string, string>;
}

/** A line still to choose a quantity for. */
export interface OutstandingChoice {
  lineNo: number;
}

const CHOICE_FIELD = "quantity_options.";
const SPLIT_FIELD = "version_splits.";

export interface DecisionProps {
  page: QuotePageView;
  /** Ex VAT. Meaningless while `outstanding` is not empty. */
  totalPence: number;
  /** Null when a price has no VAT-inclusive figure, or while a choice is outstanding. */
  totalIncVatPence: number | null;
  outstanding: OutstandingChoice[];
  /** Lines split when ordered whose versions' quantities are still to give (the total does not wait on them). */
  splitsOutstanding: OutstandingChoice[];
  choices: DecisionChoices;
  setChoices: (patch: Partial<DecisionChoices>) => void;
  busy: boolean;
  failure: string | null;
  onAccept: () => void;
  onDecline: (reason: string) => void;
  /** Open the line's quantity choice, highlighted. */
  goToLine: (lineNo: number) => void;
  /** Open the Price tab: the price line by line. */
  onOpenPrice: () => void;
}

function acceptNote(page: QuotePageView, method: PaymentMethod | null) {
  const accept = page.accept!;
  return [
    "Accepting places your order for everything on this quote, with the choices you've made.",
    method === "card" ? "You'll pay by card on the next page." : null,
    method === "credit_account" ? "We'll invoice your account." : null,
    accept.card_not_available_sentence,
    accept.proforma_refused_sentence
      ? `Bank transfer (pro-forma): ${accept.proforma_refused_sentence}`
      : null,
  ]
    .filter(Boolean)
    .join(" ");
}

function choicesStillToMake(count: number) {
  return `${count} ${count === 1 ? "choice" : "choices"} still to make`;
}

function Totals({
  page,
  totalPence,
  totalIncVatPence,
  isKnown,
  onOpenPrice,
}: Pick<
  DecisionProps,
  "page" | "totalPence" | "totalIncVatPence" | "onOpenPrice"
> & { isKnown: boolean }) {
  return (
    <SummaryTotals
      heading={page.has_alternatives ? "Total of your choices" : "Total"}
      exVatPence={isKnown ? totalPence : null}
      incVatPence={totalIncVatPence}
      onOpenPrice={onOpenPrice}
      note={
        page.lines.some(
          (l) => l.quantity_options.length === 0 && l.price_pence === null,
        )
          ? DELIVERY_NOT_YET_PRICED
          : null
      }
    />
  );
}

/** What both layouts share: what is owed, the total, how to pay, the note, Decline… and Accept. */
function DecisionContent(props: DecisionProps & { layout: "panel" | "sheet" }) {
  const {
    page,
    outstanding,
    splitsOutstanding,
    choices,
    setChoices,
    busy,
    failure,
    onAccept,
    onDecline,
    goToLine,
    layout,
  } = props;
  const toMake = [...outstanding, ...splitsOutstanding];
  const accept = page.accept!;
  const canAccept = accept.cannot_accept_sentence === null;
  const [isDeclining, setIsDeclining] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [isPoOpen, setIsPoOpen] = useState(accept.is_po_number_required);
  // A refused PO number opens the field it is bound to.
  const isPoShown = isPoOpen || Boolean(choices.errors.customer_order_number);
  const errorEntries = Object.entries(choices.errors);
  const declineId = useId();

  return (
    <div className="space-y-4">
      <div role="status" aria-live="polite">
        {toMake.length > 0 ? (
          <button
            type="button"
            className="flex w-full items-center justify-between gap-2 rounded-md border border-warning/50 bg-warning/5 px-3 py-2 text-left text-sm font-medium text-amber-900 focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none dark:text-amber-200"
            onClick={() => goToLine(toMake[0].lineNo)}
          >
            <span>{choicesStillToMake(toMake.length)}</span>
            <span aria-hidden>›</span>
          </button>
        ) : (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <CheckCircle2 className="size-4 text-green-700 dark:text-green-400" />
            All your choices are made.
          </p>
        )}
      </div>

      <Totals {...props} isKnown={outstanding.length === 0} />

      {errorEntries.length > 0 && (
        <div
          role="alert"
          className="rounded-md bg-red-50 p-2 text-sm text-red-800 dark:bg-red-950/30 dark:text-red-200"
        >
          <p className="font-medium">
            {errorEntries.length === 1
              ? "There is 1 thing to sort before you accept."
              : `There are ${errorEntries.length} things to sort before you accept.`}
          </p>
          <ul className="mt-1 list-disc pl-5">
            {errorEntries.map(([field, message]) => (
              <li key={field}>
                {field.startsWith(CHOICE_FIELD) ||
                (field.startsWith(SPLIT_FIELD) &&
                  Number.isInteger(Number(field.slice(SPLIT_FIELD.length)))) ? (
                  <button
                    type="button"
                    className="text-left underline"
                    onClick={() =>
                      goToLine(
                        Number(
                          field.slice(
                            (field.startsWith(CHOICE_FIELD)
                              ? CHOICE_FIELD
                              : SPLIT_FIELD
                            ).length,
                          ),
                        ),
                      )
                    }
                  >
                    {message}
                  </button>
                ) : (
                  message
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {failure && (
        <p role="alert" className="text-sm text-red-700 dark:text-red-400">
          {failure}
        </p>
      )}
      {!canAccept && (
        <p className="flex gap-2 text-sm text-amber-900 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          {accept.cannot_accept_sentence}
        </p>
      )}

      {canAccept && (
        <div className="space-y-3">
          <PaymentMethodChoice
            compact
            stacked
            methods={accept.payment_methods}
            value={choices.method}
            onChange={(m) => {
              const { payment_method: _, ...rest } = choices.errors;
              setChoices({ method: m || null, errors: rest });
            }}
            proformaRefusedSentence={accept.proforma_refused_sentence}
            cardNotAvailableSentence={accept.card_not_available_sentence}
            error={choices.errors.payment_method}
          />
          {isPoShown ? (
            <PoNumberField
              compact
              stacked
              value={choices.po}
              onChange={(po) => {
                const { customer_order_number: _, ...rest } = choices.errors;
                setChoices({ po, errors: rest });
              }}
              isRequired={accept.is_po_number_required}
              formatHint={poFormatHint(accept)}
              error={choices.errors.customer_order_number}
            />
          ) : (
            <Button
              variant="link"
              size="sm"
              className="h-auto p-0"
              onClick={() => setIsPoOpen(true)}
            >
              + PO number
            </Button>
          )}
          <p className="text-xs text-muted-foreground">
            {acceptNote(page, choices.method)}
          </p>
        </div>
      )}

      <div
        className={cn(
          "flex gap-2",
          layout === "panel" ? "flex-col-reverse" : "flex-row-reverse",
        )}
      >
        {canAccept && (
          <Button
            className={cn(layout === "sheet" && "flex-1")}
            onClick={onAccept}
            disabled={busy}
          >
            Accept quote ›
          </Button>
        )}
        <Button
          variant="outline"
          onClick={() => setIsDeclining((d) => !d)}
          aria-expanded={isDeclining}
          aria-controls={declineId}
          disabled={busy}
        >
          Decline…
        </Button>
      </div>

      {isDeclining && (
        <div id={declineId} className="space-y-2 rounded-md border p-3">
          <p className="font-medium">Decline this quote</p>
          <label
            htmlFor={`${declineId}-reason`}
            className="block text-sm font-medium"
          >
            Tell us why{" "}
            <span className="text-muted-foreground">(optional)</span>
          </label>
          <textarea
            id={`${declineId}-reason`}
            className="w-full rounded-md border border-input bg-background p-2 text-sm"
            rows={2}
            maxLength={1000}
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
            placeholder="For example, the timing changed or the price was too high."
          />
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsDeclining(false)}
              disabled={busy}
            >
              Back
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDecline(declineReason)}
              disabled={busy}
            >
              <XCircle className="size-4" />
              Decline quote
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Wide screens: the summary card in the page's top band, beside the header (SummaryPageFrame). */
export function QuoteDecisionPanel({
  panelRef,
  ...props
}: DecisionProps & { panelRef: React.RefObject<HTMLElement | null> }) {
  return (
    <aside
      ref={panelRef}
      tabIndex={-1}
      aria-label="Your total and accepting"
      className="focus:outline-none"
    >
      <Card className="gap-4 p-5">
        <DecisionContent {...props} layout="panel" />
      </Card>
    </aside>
  );
}

/** Narrow screens: the slim fixed bar, and the short sheet behind it. */
export function QuoteDecisionBar(props: DecisionProps) {
  const {
    page,
    outstanding,
    splitsOutstanding,
    choices,
    totalPence,
    onAccept,
  } = props;
  const accept = page.accept!;
  const canAccept = accept.cannot_accept_sentence === null;
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const totalButtonRef = useRef<HTMLButtonElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const openSheet = (e: React.MouseEvent<HTMLElement>) => {
    openerRef.current = e.currentTarget;
    setIsSheetOpen(true);
  };
  const hasProblem =
    Object.keys(choices.errors).length > 0 || props.failure !== null;
  // A refusal after Accept from the bar is shown in the sheet, where the fields are.
  useEffect(() => {
    if (!hasProblem) return;
    openerRef.current = totalButtonRef.current;
    setIsSheetOpen(true);
  }, [hasProblem]);

  // Accept from the bar goes straight through only when nothing is left to give; else the sheet shows what.
  const isReady =
    outstanding.length === 0 &&
    splitsOutstanding.length === 0 &&
    (accept.payment_methods.length === 0 || choices.method !== null) &&
    (!accept.is_po_number_required || choices.po.trim() !== "");
  const close = () => setIsSheetOpen(false);

  return (
    <>
      <div
        data-testid="quote-decision-bar"
        className="fixed inset-x-0 bottom-0 z-20 border-t bg-card/95 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] backdrop-blur"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="mx-auto flex max-w-4xl items-center gap-3 px-4 py-2.5">
          <div className="min-w-0 flex-1">
            <button
              type="button"
              ref={totalButtonRef}
              className="flex items-center gap-1 text-left font-semibold tabular-nums"
              aria-haspopup="dialog"
              aria-expanded={isSheetOpen}
              onClick={openSheet}
            >
              {outstanding.length > 0 ? "—" : formatQuotedPrice(totalPence)}
              <span className="text-xs font-normal text-muted-foreground">
                + VAT
              </span>
              <ChevronUp className="size-4 text-muted-foreground" />
            </button>
            <div role="status">
              {outstanding.length + splitsOutstanding.length > 0 && (
                <button
                  type="button"
                  className="block max-w-full truncate text-left text-xs font-medium text-amber-800 dark:text-amber-300"
                  aria-haspopup="dialog"
                  onClick={openSheet}
                >
                  {choicesStillToMake(
                    outstanding.length + splitsOutstanding.length,
                  )}
                </button>
              )}
            </div>
          </div>
          {canAccept ? (
            <Button
              onClick={(e) => (isReady ? onAccept() : openSheet(e))}
              disabled={props.busy}
            >
              Accept quote ›
            </Button>
          ) : (
            <Button variant="outline" onClick={openSheet}>
              Details ›
            </Button>
          )}
        </div>
      </div>
      {isSheetOpen && (
        <BottomSheet
          title="Accept or decline this quote"
          onClose={close}
          returnFocusTo={openerRef}
        >
          <DecisionContent
            {...props}
            layout="sheet"
            goToLine={(lineNo) => {
              close();
              props.goToLine(lineNo);
            }}
            onOpenPrice={() => {
              close();
              props.onOpenPrice();
            }}
          />
        </BottomSheet>
      )}
    </>
  );
}
